/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { clickTrack } from '@/utils/analytics/clickTrack';
import type { CustomLinkName, PDPComparisonAnalytics } from '@/types/analytics';
import { type ProductSkuDetails } from '@/features/product';
import root from 'window-or-global';
import { sessionStorage } from '@/utils/sessionStorage';

export const trackPDPComparisonAddToCart = ({
  skuId,
  partNumber,
  availableQty,
  fulfillmentTypeId,
  analyticsData: { originalPartTypeId, repositoryId, brandName, partGroupId, partTerminologyId },
  skuDetails,
  price,
  quantity = '1',
}: {
  skuId: string;
  partNumber: string | null;
  availableQty: number | undefined;
  fulfillmentTypeId: number | undefined;
  analyticsData: PDPComparisonAnalytics;
  skuDetails: ProductSkuDetails | undefined;
  price: number | undefined;
  quantity?: string;
}) => {
  const pdpComparisonAddToCart: CustomLinkName = 'PDP:AZ Product:SEO PDP Comparison Cart Addition';
  const enhancedPDPContent = sessionStorage.getItem('enhancedPDPContent') === 'true';
  clickTrack({
    customLinkName: pdpComparisonAddToCart,
    eventType: 'addToCart',
    finderFeature: 'PDP Comparison Chart',
    pageName: root.utag_data?.pageName ?? '',
    pageType: root.utag_data?.pageType ?? '',
    productAvailableQuantityList: availableQty !== undefined ? [availableQty] : [],
    productBrandList: brandName ? [brandName] : [],
    productCorePriceList: skuDetails ? [`${skuDetails.skuPricingAndAvailability.corePrice}`] : [],
    productFulfillmentTypeList: fulfillmentTypeId ? [`${fulfillmentTypeId}`] : [],
    productFindingMethod: 'Finder',
    productIdList: [skuId],
    productPartNumberList: partNumber ? [partNumber] : [],
    productPriceList: price !== undefined ? [price.toFixed(2)] : [],
    productQuantityList: [quantity],
    productRequestedQuantityList: [quantity],
    productRecommendationTypeList: 'PDP: Recommended For You',
    productRecommendationInteraction: 'productRecommendationInteraction',
    productStrikeThroughPricing: [
      skuDetails?.skuPricingAndAvailability.discountedPrice ? '1' : '0',
    ],
    ...(originalPartTypeId && { originalPartTypeIdList: [originalPartTypeId] }),
    ...(repositoryId && { productRepositoryIdList: [repositoryId] }),
    ...(partGroupId != null && { partGroupId }),
    ...(partTerminologyId != null && { partTerminologyId }),
    enhancedPDPContent,
  });
};
